/**
 * Assign Meal Plan Dialog Component
 *
 * Dialog for dietitians to assign meal plans to patients.
 * Allows selection from available meal plans and includes notes field.
 *
 * @module components/dietician-dashboard/AssignMealPlanDialog
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services/ApiService';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { TextArea } from '@/components/ui/TextArea';
import type { MealPlanFrontend, AssignMealPlanRequestFrontend } from '@/types/mealPlan';
import { getLogger } from '@/services/shared/LoggerFactory';

interface AssignMealPlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  onAssignSuccess: () => void;
}

/**
 * Assign Meal Plan Dialog Component
 */
export const AssignMealPlanDialog: React.FC<AssignMealPlanDialogProps> = ({
  isOpen,
  onClose,
  patientId,
  patientName,
  onAssignSuccess,
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();

  // State management
  const [mealPlans, setMealPlans] = useState<MealPlanFrontend[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMealPlanId, setSelectedMealPlanId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [assigning, setAssigning] = useState(false);

  // Fetch meal plans when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchMealPlans();
    }
  }, [isOpen]);

  const fetchMealPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.mealplan.getMealPlans({ page: 1, per_page: 50 });
      setMealPlans(response.mealPlans);
    } catch (error) {
      logger.error('Failed to fetch meal plans:', error);
      setError('Failed to load meal plans');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedMealPlanId) {
      setError('Please select a meal plan');
      return;
    }

    try {
      setAssigning(true);
      setError(null);

      const assignmentData: Omit<AssignMealPlanRequestFrontend, 'mealPlanId'> = {
        patientId,
        notes: notes.trim() || undefined,
      };

      await apiService.mealplan.assignMealPlan(selectedMealPlanId, assignmentData);

      // Reset form and close dialog
      setSelectedMealPlanId('');
      setNotes('');
      onAssignSuccess();
      onClose();
    } catch (error) {
      logger.error('Failed to assign meal plan:', error);
      setError('Failed to assign meal plan');
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    setSelectedMealPlanId('');
    setNotes('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg ${
        isDark ? 'bg-secondary-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div>
              <SubtitleText className="font-semibold mb-1">
                Assign Meal Plan
              </SubtitleText>
              <BodyText variant="muted" size="sm">
                Assign a meal plan to {patientName}
              </BodyText>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="p-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className={`p-4 rounded-lg mb-4 ${
              isDark ? 'bg-error-900/20 text-error-400' : 'bg-error-50 text-error-600'
            }`}>
              <BodyText>{error}</BodyText>
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-neutral-300 rounded animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <BodyText className="font-medium mb-3">
                  Select a meal plan:
                </BodyText>
                <div className="space-y-2 max-h-60 overflow-y-auto pb-3">
                  {mealPlans.map((mealPlan) => (
                    <Card
                      key={mealPlan.id}
                      className={`p-4 cursor-pointer transition-colors ${
                        selectedMealPlanId === mealPlan.id
                          ? isDark
                            ? 'bg-primary-900/20 border-primary-500'
                            : 'bg-primary-50 border-primary-500'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800'
                      }`}
                      onClick={() => setSelectedMealPlanId(mealPlan.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={selectedMealPlanId === mealPlan.id}
                          onChange={() => setSelectedMealPlanId(mealPlan.id)}
                          className="text-primary-600"
                        />
                        <div className="flex-1">
                          <BodyText className="font-medium">
                            {mealPlan.label || 'Untitled Meal Plan'}
                          </BodyText>
                          <BodyText variant="muted" size="sm">
                            Created: {new Date(mealPlan.createdAt).toLocaleDateString('en-AU')}
                          </BodyText>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div>
                <BodyText className="font-medium mb-2">
                  Notes (optional):
                </BodyText>
                <TextArea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this assignment..."
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700">
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={assigning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedMealPlanId || assigning || loading}
              loading={assigning}
            >
              {assigning ? 'Assigning...' : 'Assign Meal Plan'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};